import React, { useMemo } from 'react';
import { fmt, monthText, num } from './helpers';

export default function MonthlyRevenueChart({ rows, loading, selectedMonth, onSelectMonth }) {
  const chartRows = useMemo(() => aggregateByMonth(rows), [rows]);
  const totalProfit = chartRows.reduce((sum, row) => sum + num(row.net_profit), 0);
  const selectedRow = selectedMonth ? chartRows.find((row) => row.month === selectedMonth) : null;

  if (!loading && chartRows.length === 0) {
    return (
      <section style={cardStyle}>
        <div style={sectionHead}>
          <h3 style={sectionTitle}>월별 수익 그래프</h3>
        </div>
        <div style={emptyStyle}>그래프로 표시할 월별 수익 데이터가 없습니다.</div>
      </section>
    );
  }

  const chart = buildChart(chartRows);

  return (
    <section style={cardStyle}>
      <div style={sectionHead}>
        <div>
          <h3 style={sectionTitle}>월별 수익 그래프</h3>
          <div style={captionStyle}>막대를 클릭하면 해당 월 상세가 아래에 표시됩니다.</div>
        </div>
        <div style={selectedInfoStyle}>
          {selectedRow
            ? `${selectedRow.month} ${fmt(selectedRow.net_profit)}원`
            : `전체 ${fmt(totalProfit)}원`}
        </div>
      </div>

      <div style={chartScrollStyle}>
        <svg
          width={chart.width}
          height={chart.height}
          viewBox={`0 0 ${chart.width} ${chart.height}`}
          role="img"
          aria-label="월별 수익 그래프"
          style={{ display: 'block' }}
        >
          <line x1={chart.left} x2={chart.width - chart.right} y1={chart.top} y2={chart.top} style={gridLineStyle} />
          <line x1={chart.left} x2={chart.width - chart.right} y1={chart.zeroY} y2={chart.zeroY} style={zeroLineStyle} />
          <line x1={chart.left} x2={chart.width - chart.right} y1={chart.height - chart.bottom} y2={chart.height - chart.bottom} style={gridLineStyle} />

          {chart.ticks.map((tick) => (
            <text key={tick.key} x={chart.left - 10} y={tick.y + 4} textAnchor="end" style={axisTextStyle}>
              {tick.label}
            </text>
          ))}

          {chart.bars.map((bar) => {
            const selected = selectedMonth === bar.month;
            return (
              <g key={bar.month} onClick={() => onSelectMonth(bar.month)} style={{ cursor: 'pointer' }}>
                <rect
                  x={bar.x}
                  y={bar.y}
                  width={bar.width}
                  height={bar.height}
                  rx="3"
                  fill={bar.value < 0 ? 'rgba(220, 38, 38, 0.72)' : 'rgba(13, 148, 136, 0.78)'}
                  stroke={selected ? 'var(--text)' : 'transparent'}
                  strokeWidth={selected ? 2 : 0}
                >
                  <title>{`${bar.month}: ${fmt(bar.value)}원`}</title>
                </rect>
                <text x={bar.x + bar.width / 2} y={chart.height - 18} textAnchor="middle" style={monthTextStyle}>
                  {bar.month.slice(2)}
                </text>
                <text
                  x={bar.x + bar.width / 2}
                  y={bar.value < 0 ? Math.min(chart.height - 26, bar.y + bar.height + 14) : Math.max(14, bar.y - 7)}
                  textAnchor="middle"
                  style={valueTextStyle}
                >
                  {shortMoney(bar.value)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </section>
  );
}

function aggregateByMonth(rows = []) {
  const map = new Map();
  for (const row of rows) {
    const month = monthText(row.month);
    const prev = map.get(month) || { month, net_profit: 0 };
    prev.net_profit += num(row.net_profit);
    map.set(month, prev);
  }
  return [...map.values()].sort((a, b) => a.month.localeCompare(b.month));
}

function buildChart(rows) {
  const left = 74;
  const right = 24;
  const top = 28;
  const bottom = 44;
  const height = 260;
  const width = Math.max(760, rows.length * 86 + left + right);
  const plotHeight = height - top - bottom;
  let maxValue = Math.max(0, ...rows.map((row) => num(row.net_profit)));
  let minValue = Math.min(0, ...rows.map((row) => num(row.net_profit)));
  if (maxValue === 0 && minValue === 0) maxValue = 1;
  const range = maxValue - minValue || 1;
  const yOf = (value) => top + ((maxValue - value) / range) * plotHeight;
  const zeroY = yOf(0);
  const band = (width - left - right) / Math.max(1, rows.length);
  const barWidth = Math.min(42, Math.max(24, band * 0.48));

  return {
    width,
    height,
    left,
    right,
    top,
    bottom,
    zeroY,
    ticks: [
      { key: 'max', y: yOf(maxValue), label: shortMoney(maxValue) },
      { key: 'zero', y: zeroY, label: '0' },
      { key: 'min', y: yOf(minValue), label: shortMoney(minValue) },
    ],
    bars: rows.map((row, index) => {
      const value = num(row.net_profit);
      const valueY = yOf(value);
      const x = left + index * band + (band - barWidth) / 2;
      return {
        month: row.month,
        value,
        x,
        y: Math.min(valueY, zeroY),
        width: barWidth,
        height: Math.max(2, Math.abs(zeroY - valueY)),
      };
    }),
  };
}

function shortMoney(value) {
  const n = num(value);
  const abs = Math.abs(n);
  if (abs >= 100000000) return `${trim(n / 100000000)}억`;
  if (abs >= 10000) return `${trim(n / 10000)}만`;
  return fmt(Math.round(n));
}

function trim(value) {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

const cardStyle = { border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-card)', padding: 12, display: 'grid', gap: 10 };
const sectionHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' };
const sectionTitle = { margin: 0, fontSize: 14, color: 'var(--text)' };
const captionStyle = { marginTop: 3, color: 'var(--text-muted)', fontSize: 11 };
const selectedInfoStyle = { color: 'var(--text)', fontSize: 13, fontWeight: 850 };
const chartScrollStyle = { overflowX: 'auto', overflowY: 'hidden', paddingBottom: 2 };
const gridLineStyle = { stroke: 'var(--border)', strokeWidth: 1 };
const zeroLineStyle = { stroke: 'rgba(100, 116, 139, 0.55)', strokeWidth: 1.5 };
const axisTextStyle = { fill: 'var(--text-muted)', fontSize: 11, fontWeight: 700 };
const monthTextStyle = { fill: 'var(--text-muted)', fontSize: 11, fontWeight: 750 };
const valueTextStyle = { fill: 'var(--text)', fontSize: 10, fontWeight: 800 };
const emptyStyle = { minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13 };
